import { parseDisplayAttributes } from 'utils/ducted-ratings';

const BTU_PER_TON = 12000;

export type ComponentPosition = 'Outdoor' | 'Indoor' | 'Blower';

function positiveNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
}

function formatThousands(btu: number): string {
  const thousands = Math.round((btu / 1000) * 10) / 10;
  const text =
    thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1);
  return `${text}k`;
}

function formatBtuSpan(minBtu: number, maxBtu: number): string {
  const low = Math.min(minBtu, maxBtu);
  const high = Math.max(minBtu, maxBtu);
  const minLabel = formatThousands(low);
  const maxLabel = formatThousands(high);
  if (minLabel === maxLabel) return minLabel;
  return `${minLabel.slice(0, -1)}–${maxLabel}`;
}

function tonnageSize(spec: Record<string, unknown> | undefined): string | null {
  if (!spec) return null;
  const tonnage = positiveNumber(spec.Tonnage);
  if (tonnage !== null) return formatThousands(tonnage * BTU_PER_TON);
  const min = positiveNumber(spec.MinTonnage);
  const max = positiveNumber(spec.MaxTonnage);
  if (min !== null && max !== null) {
    return formatBtuSpan(min * BTU_PER_TON, max * BTU_PER_TON);
  }
  if (min !== null) return formatThousands(min * BTU_PER_TON);
  if (max !== null) return formatThousands(max * BTU_PER_TON);
  return null;
}

function btuSize(value: unknown): string | null {
  const btu = positiveNumber(value);
  return btu === null ? null : formatThousands(btu);
}

function displayTonnageSize(
  combination: Record<string, unknown>
): string | null {
  const { tonnage } = parseDisplayAttributes(combination);
  if (!tonnage) return null;
  return formatBtuSpan(tonnage.min * BTU_PER_TON, tonnage.max * BTU_PER_TON);
}

function derivedCoolingSize(
  combination: Record<string, unknown>
): string | null {
  return btuSize(
    parseDisplayAttributes(combination).derived.coolingCapacity?.value
  );
}

export function componentSizeLabel(
  position: ComponentPosition,
  spec: Record<string, unknown> | undefined,
  combination: Record<string, unknown>
): string | null {
  if (position === 'Outdoor') {
    return (
      tonnageSize(spec) ??
      displayTonnageSize(combination) ??
      btuSize(spec?.CoolingCapacity) ??
      btuSize(combination.CoolingCapacity) ??
      derivedCoolingSize(combination) ??
      btuSize(spec?.HeatingCapacity)
    );
  }
  if (position === 'Indoor') {
    return tonnageSize(spec) ?? btuSize(spec?.CoolingCapacity);
  }
  return btuSize(spec?.HeatingCapacity) ?? tonnageSize(spec);
}
